import { createContext, useContext, useMemo, type CSSProperties, type ReactNode } from "react";
import { createOrbitClient } from "./client";
import type { OrbitClient, OrbitClientConfig, OrbitEmbedTheme, OrbitEmbedThemeTokens } from "./types";

const OrbitCtx = createContext<OrbitClient | null>(null);

export interface OrbitProviderProps extends OrbitClientConfig {
  children: ReactNode;
  /**
   * Controls widget skinning. Use `inherit` when embedding inside a client
   * dashboard so Orbit picks up the host app's font, accent, and surface tone.
   */
  theme?: OrbitEmbedTheme;
}

/**
 * Wrap the host app (or just the embed island) with <OrbitProvider> so
 * every widget shares one client + token refresher.
 *
 *     <OrbitProvider
 *       endpoint="https://<project>.supabase.co/functions/v1/orbit-bridge"
 *       anonKey={import.meta.env.VITE_ORBIT_ANON_KEY}
 *       token={async () => fetch('/api/orbit-bridge-token').then((r) => r.json()).then((d) => d.token)}
 *     >
 *       <OrbitFeedback />
 *     </OrbitProvider>
 */
export function OrbitProvider({ endpoint, anonKey, token, theme = "inherit", children }: OrbitProviderProps) {
  const client = useMemo(
    () => createOrbitClient({ endpoint, anonKey, token }),
    [endpoint, anonKey, token],
  );
  const { mode, style } = useMemo(() => resolveTheme(theme), [theme]);

  return (
    <OrbitCtx.Provider value={client}>
      <div className="orbit-embed-provider" data-orbit-theme={mode} style={style}>
        {children}
      </div>
    </OrbitCtx.Provider>
  );
}

export function useOrbit(): OrbitClient {
  const client = useContext(OrbitCtx);
  if (!client) {
    throw new Error("orbit-embed: useOrbit() must be used inside <OrbitProvider>");
  }
  return client;
}

function resolveTheme(theme: OrbitEmbedTheme): { mode: string; style: CSSProperties } {
  if (typeof theme === "string") {
    return { mode: theme, style: {} };
  }

  const tokens = theme as OrbitEmbedThemeTokens;
  const style = {
    ...(tokens.accentColor ? { "--orbit-widget-accent": tokens.accentColor } : {}),
    ...(tokens.backgroundColor ? { "--orbit-widget-bg": tokens.backgroundColor } : {}),
    ...(tokens.surfaceColor ? { "--orbit-widget-surface": tokens.surfaceColor } : {}),
    ...(tokens.borderColor ? { "--orbit-widget-border": tokens.borderColor } : {}),
    ...(tokens.textColor ? { "--orbit-widget-text": tokens.textColor } : {}),
    ...(tokens.mutedTextColor ? { "--orbit-widget-text-dim": tokens.mutedTextColor } : {}),
    ...(tokens.radius ? { "--orbit-widget-radius": tokens.radius } : {}),
    ...(tokens.fontFamily ? { "--orbit-widget-font-family": tokens.fontFamily } : {}),
  } as CSSProperties;

  return { mode: tokens.mode || "inherit", style };
}
